/**
 * Length Contraction Visualization
 * Visualizes the equation L' = L / γ_f from the Theory of Sovereign Reflectivity
 */



// Main p5.js sketch
const lengthContractionSketch = (p) => {
    // Configuration
    const config = {
        colors: {
            primary: [44, 62, 80], // #2C3E50 (Deep indigo)
            secondary: [142, 68, 173], // #8E44AD (Cosmic purple)
            accent: [241, 196, 15], // #F1C40F (Gold)
            background: [18, 18, 18], // #121212 (Near-black)
            text: [245, 245, 245], // #F5F5F5 (Off-white)
            lowFocus: [231, 76, 60], // #E74C3C (Red)
            highFocus: [46, 204, 113] // #2ECC71 (Green)
        },
        showComparison: false
    };

    // Variables for the equation
    let focus = 0.5; // Focus intensity (F)
    let resonance = 0.5; // Vibrational resonance (R)
    let gammaF = 2.0; // Focus factor (calculated)
    let contractionRatio = 0.5; // Length contraction ratio (calculated)

    // UI elements
    let focusSlider, resonanceSlider;
    let focusValue, resonanceValue, gammaValue, distanceValue;
    let resetButton, toggleComparisonButton;

    // Animation variables
    let time = 0;
    let objects = [];
    let baselineLength = 200;

    // Setup function
    p.setup = function () {
        // Create canvas
        const canvas = p.createCanvas(p.windowWidth > 800 ? 800 : p.windowWidth - 40, 500);
        const canvasContainer = document.getElementById('length-contraction-canvas');
        if (canvasContainer) {
            canvas.parent('length-contraction-canvas');
        } else {
            console.warn('Length contraction canvas container not found, visualization may not display correctly');
        }

        // Initialize UI elements from DOM
        focusSlider = document.getElementById('focus-slider');
        resonanceSlider = document.getElementById('resonance-slider');
        focusValue = document.getElementById('focus-value');
        resonanceValue = document.getElementById('resonance-value');
        gammaValue = document.getElementById('gamma-value');
        distanceValue = document.getElementById('distance-value');
        resetButton = document.getElementById('reset-button');
        toggleComparisonButton = document.getElementById('toggle-comparison');

        // Add event listeners
        if (focusSlider) focusSlider.addEventListener('input', updateFocus);
        if (resonanceSlider) resonanceSlider.addEventListener('input', updateResonance);
        if (resetButton) resetButton.addEventListener('click', resetValues);
        if (toggleComparisonButton) toggleComparisonButton.addEventListener('click', toggleComparison);

        // Initialize objects
        createObjects();

        // Calculate initial gamma and contraction
        calculateGamma();
    };

    // Draw function
    p.draw = function () {
        p.background(config.colors.background);

        if (config.showComparison) {
            drawComparisonView();
        } else {
            drawStandardView();
        }

        // Update animation time
        time += 0.01;
    };

    // Create objects for visualization
    function createObjects() {
        objects = [
            { name: "Normal Space", color: config.colors.lowFocus, contracted: false, y: p.height / 3 },
            { name: "Contracted Space", color: config.colors.highFocus, contracted: true, y: p.height * 2 / 3 }
        ];
    }

    // Update focus from slider
    function updateFocus() {
        focus = parseFloat(focusSlider.value);
        focusValue.textContent = focus.toFixed(2);
        calculateGamma();
    }

    // Update resonance from slider
    function updateResonance() {
        resonance = parseFloat(resonanceSlider.value);
        resonanceValue.textContent = resonance.toFixed(2);
        calculateGamma();
    }

    // Calculate focus factor and contraction ratio
    function calculateGamma() {
        // Formula inspired by relativistic gamma factor: γ = 1/√(1-v²/c²)
        // Here, focus acts like velocity (v/c), higher focus = higher "velocity"
        // Resonance acts as a multiplier

        // Effective focus (enhanced by resonance)
        const effectiveFocus = focus * (1 + resonance);

        // Cap at 0.99 to avoid infinity
        const cappedFocus = Math.min(effectiveFocus, 0.99);

        // Calculate gamma using a formula similar to relativistic gamma
        gammaF = 1 / Math.sqrt(1 - cappedFocus * cappedFocus);

        // Calculate contraction ratio (L'/L = 1/γ)
        contractionRatio = 1 / gammaF;

        // Update UI
        gammaValue.textContent = gammaF.toFixed(2);
        const percentOriginal = (contractionRatio * 100).toFixed(0);
        distanceValue.textContent = `${percentOriginal}% of original`;
    }

    // Reset all values to defaults
    function resetValues() {
        focusSlider.value = 0.5;
        resonanceSlider.value = 0.5;
        updateFocus();
        updateResonance();
    }

    // Toggle comparison view
    function toggleComparison() {
        config.showComparison = !config.showComparison;
        toggleComparisonButton.textContent = config.showComparison ? 'Hide Comparison' : 'Toggle Comparison';
    }

    // Draw standard visualization view
    function drawStandardView() {
        // Draw background grid
        drawGrid();

        // Draw objects
        drawObjects();

        // Draw focus field
        drawFocusField();

        // Draw labels and equation
        drawLabels();
    }

    // Draw objects
    function drawObjects() {
        for (let object of objects) {
            // Calculate object length
            let objectLength;
            if (object.contracted) {
                objectLength = baselineLength * contractionRatio;
            } else {
                objectLength = baselineLength;
            }

            // Draw object container
            const centerX = p.width / 2;
            const startX = centerX - objectLength / 2;
            const endX = centerX + objectLength / 2;

            // Draw object
            p.stroke(object.color);
            p.strokeWeight(3);
            p.noFill();
            p.rect(startX, object.y - 30, objectLength, 60, 10);

            // Draw internal structure (grid lines)
            p.stroke(object.color[0], object.color[1], object.color[2], 100);
            p.strokeWeight(1);

            const gridCount = 10;
            const gridSpacing = objectLength / gridCount;

            for (let i = 1; i < gridCount; i++) {
                const x = startX + i * gridSpacing;
                p.line(x, object.y - 25, x, object.y + 25);
            }

            // Draw object name
            p.noStroke();
            p.fill(object.color);
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(16);
            p.text(object.name, centerX, object.y - 50);

            // Draw length measurement
            p.stroke(config.colors.accent);
            p.strokeWeight(1);
            p.line(startX, object.y + 40, endX, object.y + 40);
            p.line(startX, object.y + 35, startX, object.y + 45);
            p.line(endX, object.y + 35, endX, object.y + 45);

            // Draw length value
            p.noStroke();
            p.fill(config.colors.text);
            p.textSize(14);
            if (object.contracted) {
                p.text(`Length: ${objectLength.toFixed(0)} units (${(contractionRatio * 100).toFixed(0)}%)`, centerX, object.y + 60);
            } else {
                p.text("Length: 200 units (100%)", centerX, object.y + 60);
            }
        }

        // Draw connecting arrow
        const arrowStartY = objects[0].y + 80;
        const arrowEndY = objects[1].y - 80;
        const arrowX = p.width / 2;

        p.stroke(config.colors.accent);
        p.strokeWeight(2);
        p.fill(config.colors.accent);
        drawArrow(arrowX, arrowStartY, arrowX, arrowEndY);

        // Draw arrow label
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(14);
        p.text("Focus Field Effect", arrowX + 80, (arrowStartY + arrowEndY) / 2);
    }

    // Draw focus field visualization
    function drawFocusField() {
        const centerX = p.width / 2;
        const centerY = objects[1].y;
        const radius = 30;

        // Draw focus field
        const fieldRadius = radius * (0.5 + focus * 0.5);
        const fieldIntensity = 100 + focus * 155;

        // Draw concentric circles representing focus field
        for (let i = 0; i < 3; i++) {
            const alpha = p.map(i, 0, 3, fieldIntensity, 20);
            p.noFill();
            p.stroke(config.colors.highFocus[0], config.colors.highFocus[1], config.colors.highFocus[2], alpha);
            p.strokeWeight(2);
            p.ellipse(centerX, centerY, fieldRadius * 2 - i * 10);
        }

        // Draw resonance waves
        const waveCount = 8;
        const waveAmplitude = 10 * resonance;

        p.noFill();
        p.stroke(config.colors.accent[0], config.colors.accent[1], config.colors.accent[2], 150);
        p.strokeWeight(2);

        p.beginShape();
        for (let i = 0; i <= waveCount; i++) {
            const angle = p.map(i, 0, waveCount, 0, p.TWO_PI);
            const r = fieldRadius + Math.sin(angle * 3 + time * 2) * waveAmplitude;
            const x = centerX + Math.cos(angle) * r;
            const y = centerY + Math.sin(angle) * r;
            p.vertex(x, y);
        }
        p.endShape();
    }

    // Draw background grid
    function drawGrid() {
        p.stroke(config.colors.primary[0], config.colors.primary[1], config.colors.primary[2], 50);
        p.strokeWeight(1);

        const gridSize = 30;
        for (let x = 0; x < p.width; x += gridSize) {
            p.line(x, 0, x, p.height);
        }

        for (let y = 0; y < p.height; y += gridSize) {
            p.line(0, y, p.width, y);
        }
    }

    // Draw comparison view (low vs high focus)
    function drawComparisonView() {
        // Draw dividing line
        p.stroke(config.colors.accent);
        p.strokeWeight(2);
        p.line(p.width / 2, 0, p.width / 2, p.height);

        // Draw titles
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(16);
        p.text("Low Focus (F = 0.2, R = 0.2)", p.width * 0.25, 30);
        p.text("High Focus (F = 0.9, R = 0.9)", p.width * 0.75, 30);

        // Draw equations
        p.fill(config.colors.accent);
        p.textSize(20);
        p.text(`γ = 1.02`, p.width * 0.25, 60);
        p.text(`γ = 5.26`, p.width * 0.75, 60);

        // Draw low focus scenario (left side)
        drawFocusScenario(p.width * 0.25, p.height / 2, 0.2, 0.2);

        // Draw high focus scenario (right side)
        drawFocusScenario(p.width * 0.75, p.height / 2, 0.9, 0.9);

        // Draw explanation
        p.fill(config.colors.text);
        p.textSize(14);
        p.textAlign(p.CENTER, p.CENTER);
        p.text("Minimal contraction", p.width * 0.25, p.height - 40);
        p.text("Significant contraction", p.width * 0.75, p.height - 40);
    }

    // Draw a focus scenario for the comparison view
    function drawFocusScenario(centerX, centerY, focusValue, resonanceValue) {
        // Calculate gamma for this scenario
        const effectiveFocus = focusValue * (1 + resonanceValue);
        const cappedFocus = Math.min(effectiveFocus, 0.99);
        const scenarioGamma = 1 / Math.sqrt(1 - cappedFocus * cappedFocus);
        const scenarioContraction = 1 / scenarioGamma;

        // Calculate object length
        const objectLength = baselineLength * scenarioContraction;
        const startX = centerX - objectLength / 2;
        const endX = centerX + objectLength / 2;

        // Draw object
        p.stroke(p.lerpColor(
            p.color(config.colors.lowFocus),
            p.color(config.colors.highFocus),
            focusValue
        ));
        p.strokeWeight(3);
        p.noFill();
        p.rect(startX, centerY - 30, objectLength, 60, 10);

        // Draw internal structure (grid lines)
        p.stroke(config.colors.secondary[0], config.colors.secondary[1], config.colors.secondary[2], 100);
        p.strokeWeight(1);

        const gridCount = 10;
        const gridSpacing = objectLength / gridCount;

        for (let i = 1; i < gridCount; i++) {
            const x = startX + i * gridSpacing;
            p.line(x, centerY - 25, x, centerY + 25);
        }

        // Draw length measurement
        p.stroke(config.colors.accent);
        p.strokeWeight(1);
        p.line(startX, centerY + 40, endX, centerY + 40);
        p.line(startX, centerY + 35, startX, centerY + 45);
        p.line(endX, centerY + 35, endX, centerY + 45);

        // Draw length value
        p.noStroke();
        p.fill(config.colors.text);
        p.textSize(14);
        p.text(`Length: ${objectLength.toFixed(0)} units (${(scenarioContraction * 100).toFixed(0)}%)`, centerX, centerY + 60);

        // Draw focus field
        const fieldRadius = 30;
        const fieldIntensity = 100 + focusValue * 155;

        // Draw concentric circles representing focus field
        for (let i = 0; i < 3; i++) {
            const alpha = p.map(i, 0, 3, fieldIntensity, 20);
            p.noFill();
            p.stroke(config.colors.highFocus[0], config.colors.highFocus[1], config.colors.highFocus[2], alpha);
            p.strokeWeight(2);
            p.ellipse(centerX, centerY - 80, fieldRadius * 2 - i * 10);
        }

        // Draw resonance waves if resonance is significant
        if (resonanceValue > 0.3) {
            const waveCount = 8;
            const waveAmplitude = 10 * resonanceValue;

            p.noFill();
            p.stroke(config.colors.accent[0], config.colors.accent[1], config.colors.accent[2], 150);
            p.strokeWeight(2);

            p.beginShape();
            for (let i = 0; i <= waveCount; i++) {
                const angle = p.map(i, 0, waveCount, 0, p.TWO_PI);
                const r = fieldRadius + Math.sin(angle * 3 + time * 2) * waveAmplitude;
                const x = centerX + Math.cos(angle) * r;
                const y = (centerY - 80) + Math.sin(angle) * r;
                p.vertex(x, y);
            }
            p.endShape();
        }

        // Draw focus label
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(12);
        p.text(`Focus: ${focusValue.toFixed(1)}`, centerX, centerY - 120);
    }

    // Helper function to draw arrows
    function drawArrow(x1, y1, x2, y2) {
        p.line(x1, y1, x2, y2);

        const angle = p.atan2(y2 - y1, x2 - x1);
        const arrowSize = 10;

        p.push();
        p.translate(x2, y2);
        p.rotate(angle);
        p.triangle(-arrowSize, arrowSize / 2, -arrowSize, -arrowSize / 2, 0, 0);
        p.pop();
    }

    // Draw labels and equation
    function drawLabels() {
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);

        // Draw equation at the top
        p.textSize(24);
        p.fill(config.colors.accent);
        p.text(`L' = L / γ_f`, p.width / 2, 30);

        // Draw current values
        p.textSize(16);
        p.fill(config.colors.text);
        p.text(`F = ${focus.toFixed(2)}   R = ${resonance.toFixed(2)}   γ = ${gammaF.toFixed(2)}`, p.width / 2, 60);
    }

    // Handle window resize
    p.windowResized = function () {
        p.resizeCanvas(p.windowWidth > 800 ? 800 : p.windowWidth - 40, 500);
    };
};

// Initialize the p5.js sketch
new p5(lengthContractionSketch);
